
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import {
	ModuleContentTitleTrend,
	MovieOrShowOrSeason,
	ObjectType,
	Season,
	StreamingChartsTitlesEdge,
} from '@/@types/graphql-types';
import { HomeModuleTitleFragment } from '@/components/home/graphql/queries/GetHomeModulesV2.query';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import TitleDetailStreamingChartList from '@/components/details/streaming-chart/TitleDetailStreamingChartList.vue';

const Language = namespace('language');

@Component({
	name: 'TitleDetailStreamingChart',
	components: {
		TitleDetailStreamingChartList,
	},
})
export default class TitleDetailStreamingChart extends Vue {
	@Prop({ required: true }) titles: StreamingChartsTitlesEdge[];
	@Prop({ required: true }) titleId: string;
	@Prop() titleObjectType: ObjectType;
	@Prop({ required: true }) titleName: string;
	@Prop() lastUpdated: string | number | Date;
	@Prop({ default: false }) isSidebar: boolean;

	@Language.Getter language: string;
	@Language.Getter country: string;

	get renderChart() {
		return this.titles.length > 0;
	}

	get titleDetailTitle() {
		return this.titles.find((title: StreamingChartsTitlesEdge) => {
			return title.node.id === this.titleId;
		});
	}

	get titleNodes(): MovieOrShowOrSeason[] {
		return this.titles.map((title: StreamingChartsTitlesEdge) => {
			return title.node;
		});
	}

	getAdjacentTitle(indexShift: number) {
		const thisTitleIndex = this.titleNodes.findIndex((title: MovieOrShowOrSeason) => title.id === this.titleId);
		return this.titleNodes[thisTitleIndex + indexShift];
	}

	handleClick(event: { index: number; title: HomeModuleTitleFragment }) {
		this.trackClick(event);
	}

	trackClick(event: { index: number; title: HomeModuleTitleFragment }) {
		TrackingHelper.trackEvent('userinteraction', {
			action: 'title_clicked',
			label: `${this.titleId}, ${event.title.id}`,
			property: 'streaming_chart',
			value: event.index,
		});
	}

	get provideStreamingChartSpinningText() {
		const titleNode = this.titleDetailTitle?.node as MovieOrShowOrSeason as Season;
		const titleName = titleNode?.show?.content?.title ?? titleNode?.content?.title;
		const streamingChartInfo = this.titleDetailTitle?.streamingChartInfo;
		const objectType = titleNode?.objectType;

		let country = this.$t('WEBAPP_COUNTRY_' + this.country);
		if (this.country === 'US') country = this.$t('WEBAPP_COUNTRY_THE_US');
		if (this.country === 'GB') country = this.$t('WEBAPP_COUNTRY_THE_GB');

		const currentRank = this.$t('WEBAPP_STREAMING_CHART_SPINNINGTEXT_CURRENTRANK', {
			title: titleName,
			rank: streamingChartInfo?.rank,
		});

		let rankMovement;
		if (streamingChartInfo) {
			if (streamingChartInfo.trend === ModuleContentTitleTrend.Up) {
				rankMovement =
					streamingChartInfo.trendDifference === 1
						? this.$t(
								`WEBAPP_STREAMING_CHART_SPINNINGTEXT_${
									objectType === 'MOVIE' ? 'MOVIE' : 'SHOW'
								}_RANKINCREASE1`,
								{
									rank_change: streamingChartInfo.trendDifference,
								}
						  )
						: this.$t(
								`WEBAPP_STREAMING_CHART_SPINNINGTEXT_${
									objectType === 'MOVIE' ? 'MOVIE' : 'SHOW'
								}_RANKINCREASEMULTIPLE`,
								{
									rank_change: streamingChartInfo.trendDifference,
								}
						  );
			} else if (streamingChartInfo.trend === ModuleContentTitleTrend.Stable) {
				rankMovement = this.$tc(
					`WEBAPP_STREAMING_CHART_SPINNINGTEXT_${objectType === 'MOVIE' ? 'MOVIE' : 'SHOW'}_RANKSAME`
				);
			} else if (streamingChartInfo.trend === ModuleContentTitleTrend.Down) {
				rankMovement =
					streamingChartInfo.trendDifference === -1
						? this.$t(
								`WEBAPP_STREAMING_CHART_SPINNINGTEXT_${
									objectType === 'MOVIE' ? 'MOVIE' : 'SHOW'
								}_RANKDECREASE1`,
								{
									rank_change: streamingChartInfo.trendDifference,
								}
						  )
						: this.$t(
								`WEBAPP_STREAMING_CHART_SPINNINGTEXT_${
									objectType === 'MOVIE' ? 'MOVIE' : 'SHOW'
								}_RANKDECREASEMULTIPLE`,
								{
									rank_change: streamingChartInfo.trendDifference,
								}
						  );
			}
		}

		if (streamingChartInfo?.rank === 1) {
			return `${currentRank} ${rankMovement ?? ''}`;
		}

		let comparison;
		if (objectType === ObjectType.Movie) {
			comparison = this.$t('WEBAPP_STREAMING_CHART_SPINNINGTEXT_MOVIE_COMPARISON', {
				country,
				position_below: this.getAdjacentTitle(1)?.content?.title,
				position_above: this.getAdjacentTitle(-1)?.content?.title,
			});
		} else if (objectType === ObjectType.Show || objectType === ObjectType.ShowSeason) {
			comparison = this.$t('WEBAPP_STREAMING_CHART_SPINNINGTEXT_SHOW_COMPARISON', {
				country,
				// @ts-ignore
				position_below: this.getAdjacentTitle(1)?.show
					? // @ts-ignore
					  this.getAdjacentTitle(1)?.show?.content?.title
					: this.getAdjacentTitle(1)?.content?.title,
				// @ts-ignore
				position_above: this.getAdjacentTitle(-1)?.show
					? // @ts-ignore
					  this.getAdjacentTitle(-1)?.show?.content?.title
					: this.getAdjacentTitle(-1)?.content?.title,
			});
		}

		return `${currentRank} ${rankMovement ?? ''} ${comparison ?? ''}`;
	}
}
